import { Button } from "@izicvs/ui";
import { cn } from "@izicvs/utils";
import { motion } from "framer-motion";
import { Link } from "react-router";

import { GlowingEffect } from "@/client/components/glowing-effect";

import { Decoration } from "../../home/sections/hero/decoration";

// Branded hero for standalone marketing and legal pages. The site header is
// fixed, so we offset the hero with top padding to clear it, and reuse the
// same grid + gradient decoration as the home hero.
export const PageHero = ({
  eyebrow,
  icon: Icon,
  title,
  highlight,
  subtitle,
  className,
}: {
  eyebrow?: string;
  icon?: React.ElementType;
  title: string;
  highlight?: string;
  subtitle?: string;
  className?: string;
}) => (
  <section className={cn("relative overflow-hidden border-b border-border/40", className)}>
    <Decoration.Grid />
    <Decoration.Gradient />
    <div className="container mx-auto px-6 pb-16 pt-32 text-center lg:px-8 lg:pt-40">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }}
      >
        {eyebrow && (
          <span className="mb-5 inline-flex items-center gap-2 rounded-full border border-blue-500/20 bg-blue-500/10 px-4 py-1.5 text-sm font-medium text-blue-600 shadow-sm dark:text-blue-400">
            {Icon && <Icon className="size-4" />}
            {eyebrow}
          </span>
        )}
        <h1 className="mx-auto max-w-4xl text-4xl font-black leading-tight text-neutral-800 dark:text-neutral-100 md:text-5xl">
          {title}
          {highlight && (
            <>
              {" "}
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                {highlight}
              </span>
            </>
          )}
        </h1>
        {subtitle && (
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-neutral-600 dark:text-neutral-400">
            {subtitle}
          </p>
        )}
      </motion.div>
    </div>
  </section>
);

// Bordered icon box matching the home Services cards.
export const IconBadge = ({
  icon: Icon,
  className,
}: {
  icon: React.ElementType;
  className?: string;
}) => (
  <div className={cn("w-fit rounded-lg border border-gray-600 p-2", className)}>
    <Icon className="size-4 text-black dark:text-neutral-400" />
  </div>
);

// Standard content card, matching the home Services cards (animated glow border).
export const FeatureCard = ({
  icon: Icon,
  title,
  description,
  index = 0,
  className,
}: {
  icon: React.ElementType;
  title: React.ReactNode;
  description?: React.ReactNode;
  index?: number;
  className?: string;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0, transition: { duration: 0.4, delay: (index % 4) * 0.08 } }}
    viewport={{ once: true }}
    className={cn("min-h-56 list-none", className)}
  >
    <div className="rounded-2.5xl relative h-full border p-2 md:rounded-3xl md:p-3">
      <GlowingEffect glow spread={40} disabled={false} proximity={64} inactiveZone={0.01} />
      <div className="border-0.75 relative flex h-full flex-col justify-between gap-6 overflow-hidden rounded-xl p-6 dark:shadow-[0px_0px_27px_0px_#2D2D2D]">
        <div className="relative flex flex-1 flex-col justify-between gap-3">
          <div className="w-fit rounded-lg border border-gray-600 p-2">
            <Icon className="size-4 text-black dark:text-neutral-400" />
          </div>
          <div className="space-y-3">
            <h3 className="text-balance pt-0.5 text-xl font-semibold text-black dark:text-white md:text-2xl/[1.875rem]">
              {title}
            </h3>
            {description && (
              <p className="text-sm/[1.125rem] text-black dark:text-neutral-400 md:text-base/[1.375rem]">
                {description}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  </motion.div>
);

// Section heading with consistent typographic scale.
export const SectionHeading = ({
  title,
  subtitle,
  center = true,
  className,
}: {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  center?: boolean;
  className?: string;
}) => (
  <div className={cn("mb-12", center && "text-center", className)}>
    <h2 className="text-3xl font-bold text-neutral-800 dark:text-neutral-100 md:text-4xl">
      {title}
    </h2>
    {subtitle && (
      <p
        className={cn(
          "mt-4 text-lg leading-relaxed text-neutral-600 dark:text-neutral-400",
          center && "mx-auto max-w-2xl",
        )}
      >
        {subtitle}
      </p>
    )}
  </div>
);

// Standard page section wrapper.
export const Section = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <section className={cn("container mx-auto px-6 py-16 lg:px-8", className)}>{children}</section>
);

// Branded call-to-action band used to close marketing pages.
export const CtaBand = ({
  title,
  description,
  ctaLabel,
  to = "/auth/register",
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  ctaLabel: React.ReactNode;
  to?: string;
}) => (
  <section className="container mx-auto px-6 py-16 lg:px-8">
    <div className="relative overflow-hidden rounded-3xl border border-blue-500/20 bg-gradient-to-br from-blue-500/10 via-purple-500/5 to-transparent p-10 text-center md:p-14">
      <div className="pointer-events-none absolute -right-16 -top-16 size-48 rounded-full bg-purple-500/15 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-16 -left-16 size-48 rounded-full bg-blue-500/15 blur-3xl" />
      <h2 className="relative mx-auto max-w-2xl text-2xl font-bold text-neutral-800 dark:text-neutral-100 md:text-3xl">
        {title}
      </h2>
      {description && (
        <p className="relative mx-auto mt-3 max-w-xl text-neutral-600 dark:text-neutral-400">
          {description}
        </p>
      )}
      <Button asChild size="lg" className="relative mt-7">
        <Link to={to}>{ctaLabel}</Link>
      </Button>
    </div>
  </section>
);

// Constrained, readable column for legal / long-form content.
export const Prose = ({ children }: { children: React.ReactNode }) => (
  <section className="container mx-auto px-6 py-16 lg:px-8">
    <div className="mx-auto max-w-3xl rounded-2xl border border-border bg-background p-8 md:p-12">
      <div className="space-y-6 text-neutral-600 dark:text-neutral-400 [&_h2]:mt-10 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:text-neutral-800 first:[&_h2]:mt-0 dark:[&_h2]:text-neutral-100 [&_li]:ml-1 [&_p]:leading-relaxed [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-5">
        {children}
      </div>
    </div>
  </section>
);
